import { useEffect, useState } from 'react';
import { NotificationGroup, NotifyMessageStackGroup } from '@/shared/types/message';
import { notificationService } from '../services/NotificationService';

/**
 * Estado reactivo de los mazos de notificaciones (`notificationService`): mapa de grupos y helpers de cierre.
 *
 * @returns Objeto con:
 * - `groups` — snapshot actual de todos los grupos (incluye los en `isLeaving` hasta que el servicio los retira).
 * - `activeGroups` — valores de `groups` sin `isLeaving` (vista “solo activos”).
 * - `leavingGroups` — grupos en animación de salida.
 * - `dismissGroup` — vacía `primary` o `secondary`.
 * - `dismissStack` — vacía por `stackKey` (p. ej. `'auto'`).
 */
export const useNotification = () => {
  const [groups, setGroups] = useState<Record<string, NotificationGroup>>({});

  useEffect(() => {
    // Obtener estado inicial
    setGroups(notificationService.getGroups());

    // Suscribirse a cambios
    const unsubscribe = notificationService.subscribe((newGroups) => {
      setGroups({ ...newGroups });
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // Obtener grupos activos (no null y no saliendo)
  // Nota: Los grupos con isLeaving se excluyen de activeGroups para evitar
  // renderizar notificaciones que están en proceso de salida
  const activeGroups = Object.values(groups).filter((group) => !group.isLeaving);

  // Obtener grupos que están saliendo (para animación)
  // Estos se mantienen en el estado pero no se renderizan como "activos"
  const leavingGroups = Object.values(groups).filter((group) => group.isLeaving);

  return {
    groups,
    activeGroups,
    leavingGroups,
    dismissGroup: (stackGroup: NotifyMessageStackGroup) =>
      notificationService.dismissGroup(stackGroup),
    dismissStack: (stackKey: string) => notificationService.dismissStack(stackKey),
  };
};
